import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { TrendingDown, Lock, Sparkles, AlertTriangle, Loader2, PhoneMissed } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { PageHeader, MetricCard, Select, Button, LoadingBlock, EmptyState, StatusBadge, Card, CardContent, toast } from "@/components/ds";
import { api, formatApiError, type LostLeadData } from "@/lib/api";

const DATE_OPTIONS = [
  { value: "last_7_days", label: "Last 7 days" },
  { value: "last_30_days", label: "Last 30 days" },
  { value: "this_month", label: "This month" },
  { value: "last_90_days", label: "Last 90 days" },
];

const REASON_COLOR: Record<string, string> = {
  price_objection: "bg-rose-500", outside_service_area: "bg-amber-500", no_availability: "bg-orange-500",
  competitor_selected: "bg-fuchsia-500", caller_not_ready: "bg-sky-500", service_unavailable: "bg-violet-500",
  no_response: "bg-slate-400", unknown: "bg-slate-300",
};
const money = (n: number | null | undefined) => (n == null ? "—" : `$${Number(n).toLocaleString()}`);
const tidy = (s: string | null) => String(s ?? "").replace(/_/g, " ");

function LockedCard({ message }: { message?: string }) {
  return (
    <Card data-testid="lostleads-locked">
      <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100"><Lock className="h-6 w-6 text-slate-400" /></div>
        <h3 className="text-lg font-semibold text-slate-900">Lost Lead Analysis</h3>
        <p className="max-w-md text-sm text-slate-500">{message || "This feature is available on the Growth plan and above."}</p>
        <Button onClick={() => (window.location.href = "/app/billing")} data-testid="lostleads-upgrade">View plans</Button>
      </CardContent>
    </Card>
  );
}

function ReasonBars({ reasons }: { reasons: NonNullable<LostLeadData["reasons"]> }) {
  const max = Math.max(1, ...reasons.map((r) => r.count));
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="lostleads-reasons">
      <p className="mb-3 text-sm font-semibold text-slate-800">Top reasons</p>
      <div className="space-y-2">
        {reasons.map((r) => (
          <div key={r.reason} className="flex items-center gap-3" data-testid={`lostleads-reason-${r.reason}`}>
            <span className="w-36 shrink-0 text-xs text-slate-600">{r.label}</span>
            <div className="h-4 flex-1 overflow-hidden rounded bg-slate-100">
              <div className={`h-full ${REASON_COLOR[r.reason] || "bg-slate-300"}`} style={{ width: `${(r.count / max) * 100}%` }} />
            </div>
            <span className="w-8 shrink-0 text-right text-xs font-medium text-slate-700">{r.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function LostLeads() {
  const qc = useQueryClient();
  const [dateFilter, setDateFilter] = useState("last_30_days");
  const params = useMemo(() => ({ date_filter: dateFilter }), [dateFilter]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["lost-leads", dateFilter],
    queryFn: () => api.lostLeads(params),
  });
  const { data: meta } = useQuery({ queryKey: ["lost-leads-meta"], queryFn: api.lostLeadsMeta });

  const analyze = useMutation({
    mutationFn: () => api.lostLeadsAnalyze({ date_filter: dateFilter }),
    onSuccess: (r) => {
      if (r.available) { toast.success(`Analyzed ${r.classified ?? 0} lost leads`); qc.invalidateQueries({ queryKey: ["lost-leads"] }); }
      else toast.error(r.message || "Analysis unavailable");
    },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const correct = useMutation({
    mutationFn: ({ key, reason }: { key: string; reason: string }) => api.lostLeadCorrect(key, reason),
    onSuccess: () => { toast.success("Reason updated"); qc.invalidateQueries({ queryKey: ["lost-leads"] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const header = (
    <PageHeader
      title="Lost Leads"
      testid="lostleads-header"
      description="Why opportunities didn't convert. All figures are for your organization only and based on real interactions."
      action={<Select value={dateFilter} onChange={setDateFilter} options={DATE_OPTIONS} className="w-[160px]" testid="lostleads-date-filter" />}
    />
  );

  if (isLoading) return <div data-testid="page-lost-leads" className="space-y-6">{header}<LoadingBlock rows={4} /></div>;
  if (isError || !data) return <div data-testid="page-lost-leads" className="space-y-6">{header}<EmptyState title="Couldn't load lost leads" description="Please try again." action={<Button onClick={() => refetch()}>Retry</Button>} /></div>;

  if (!data.available) return <div data-testid="page-lost-leads" className="space-y-6">{header}<LockedCard message={data.message} /></div>;

  const ent = data.entitlements || {};
  const reasonOptions = (meta?.reasons || []).map((r) => ({ value: r.value, label: r.label }));

  return (
    <div data-testid="page-lost-leads" className="space-y-6">
      {header}

      {/* Metric cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <MetricCard label="Lost leads" value={String(data.total ?? 0)} icon={TrendingDown} testid="lostleads-total" />
        {ent.impact && (
          <MetricCard label="Estimated impact" value={money(data.estimated_impact)} icon={AlertTriangle} testid="lostleads-impact"
            hint={data.impact_missing_count ? `${data.impact_missing_count} without an estimate` : undefined} />
        )}
        {ent.reasons && (
          <MetricCard label="Awaiting AI review" value={String(data.unclassified_count ?? 0)} icon={Sparkles} testid="lostleads-unclassified" />
        )}
      </div>

      {/* Growth basic-only upsell */}
      {data.basic_only && (
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="lostleads-by-channel">
            <p className="mb-3 text-sm font-semibold text-slate-800">By channel</p>
            <div className="flex flex-wrap gap-4">
              {Object.entries(data.by_channel || {}).map(([ch, n]) => (
                <div key={ch} className="flex items-center gap-2 text-sm text-slate-700">
                  <PhoneMissed className="h-4 w-4 text-slate-400" /> <span className="capitalize">{ch}</span>
                  <span className="font-semibold text-slate-900">{n}</span>
                </div>
              ))}
              {Object.keys(data.by_channel || {}).length === 0 && <span className="text-sm text-slate-400">No lost leads in this range.</span>}
            </div>
          </div>
          <p className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-700" data-testid="lostleads-upsell">
            You're seeing basic lost-lead counts. Upgrade to <b>Pro</b> for full Lost Lead Intelligence — AI-categorized reasons,
            trends, estimated opportunity impact and staff corrections.
          </p>
        </div>
      )}

      {/* Pro / custom intelligence */}
      {ent.reasons && (
        <>
          {(data.unclassified_count ?? 0) > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="lostleads-analyze-bar">
              <span className="flex items-center gap-2 text-sm text-amber-800">
                <Sparkles className="h-4 w-4" />
                {data.llm_configured
                  ? `${data.unclassified_count} lost lead(s) haven't been categorized yet.`
                  : "AI is not configured on the server, so automatic categorization is unavailable. You can still set reasons manually below."}
              </span>
              {data.llm_configured && (
                <Button size="sm" onClick={() => analyze.mutate()} disabled={analyze.isPending} data-testid="lostleads-analyze-btn">
                  {analyze.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Analyze with AI
                </Button>
              )}
            </div>
          )}

          <div className="grid gap-4 lg:grid-cols-2">
            {data.reasons && data.reasons.length > 0 && <ReasonBars reasons={data.reasons} />}
            {ent.trends && (
              <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="lostleads-trend">
                <p className="mb-3 text-sm font-semibold text-slate-800">Trend</p>
                {(data.trend || []).length === 0 ? (
                  <div className="flex h-[200px] items-center justify-center text-sm text-slate-400">No data in this range.</div>
                ) : (
                  <div style={{ width: "100%", height: 200 }}>
                    <ResponsiveContainer>
                      <LineChart data={data.trend}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => String(d).slice(5)} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Line type="monotone" dataKey="count" stroke="#ef4444" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}
          </div>

          {ent.impact && data.impact_note && <p className="text-xs text-amber-600" data-testid="lostleads-impact-note">{data.impact_note}</p>}

          {/* Drill-down + correction */}
          <div className="rounded-xl border border-slate-200 bg-white" data-testid="lostleads-items">
            <div className="border-b border-slate-100 px-5 py-3 text-sm font-semibold text-slate-800">Lost lead details</div>
            {(data.items || []).length === 0 ? (
              <div className="px-5 py-10 text-center text-sm text-slate-400">No lost leads in this range.</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {(data.items || []).map((it) => (
                  <div key={it.id} className="flex flex-col gap-2 px-5 py-3 sm:flex-row sm:items-center sm:justify-between" data-testid={`lostleads-item-${it.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-900">{it.contact_name || it.contact_phone || "Unknown contact"}</span>
                        <StatusBadge tone="slate">{tidy(it.channel)}</StatusBadge>
                        {it.reason_source === "staff" && <StatusBadge tone="blue">staff</StatusBadge>}
                        {it.reason_source === "ai" && <StatusBadge tone="green">AI</StatusBadge>}
                        {it.reason_source === "unclassified" && <StatusBadge tone="amber">unreviewed</StatusBadge>}
                        {it.estimated_value != null && <span className="text-xs text-slate-500">{money(it.estimated_value)}</span>}
                      </div>
                      {it.summary && <p className="mt-0.5 line-clamp-2 text-xs text-slate-500">{it.summary}</p>}
                      {it.rationale && <p className="mt-0.5 text-xs italic text-slate-400">AI: {it.rationale}</p>}
                    </div>
                    <div className="w-full sm:w-48">
                      {ent.correct ? (
                        <Select
                          value={it.reason}
                          onChange={(v) => correct.mutate({ key: it.id, reason: v })}
                          options={reasonOptions}
                          testid={`lostleads-reason-select-${it.id}`}
                        />
                      ) : (
                        <StatusBadge tone="slate">{tidy(it.reason)}</StatusBadge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
